import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import {
  ApiError,
  endpoints,
  type DataImport,
  type ImportSummary,
} from "../services/api";

const STATUS_LABEL: Record<DataImport["status"], string> = {
  PENDING: "W kolejce",
  PROCESSING: "Przetwarzanie",
  DONE: "Gotowe",
  ERROR: "Błąd",
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" });
}

export default function ImportPage() {
  const { t } = useTranslation();
  const { currentStoreId } = useAuth();
  const [imports, setImports] = useState<DataImport[]>([]);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refreshImports = useCallback(async () => {
    if (currentStoreId === null) {
      setImports([]);
      return;
    }
    try {
      const list = await endpoints.imports(currentStoreId, 20);
      setImports(list);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Błąd ładowania historii");
    }
  }, [currentStoreId]);

  useEffect(() => {
    refreshImports();
  }, [refreshImports]);

  const upload = useCallback(
    async (file: File) => {
      if (currentStoreId === null) {
        setError("Wybierz sklep przed wgraniem pliku.");
        return;
      }
      setBusy(true);
      setError(null);
      setSummary(null);
      try {
        const res = await endpoints.uploadCSV(file, currentStoreId);
        setSummary(res);
        await refreshImports();
      } catch (e) {
        setError(e instanceof ApiError ? e.message : "Błąd uploadu");
      } finally {
        setBusy(false);
      }
    },
    [currentStoreId, refreshImports],
  );

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  }

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) upload(file);
    e.target.value = "";
  }

  return (
    <Layout>
      <header className="page-header">
        <h1>{t("nav.import")}</h1>
      </header>

      <section className="card">
        <h2 className="card__title">XML z Symplex Small Business (zalecane)</h2>
        <p>
          Eksport z Symplexa: <strong>Raporty → „Wyciąg z dokumentów sprzedaży" →
          F3 Eksport do XML</strong>. Plik możesz spakować do .zip żeby zmniejszyć rozmiar
          (4 MB XML → ok. 100 KB ZIP). <strong>Jeden plik na archiwum</strong> — jeśli wgrasz
          ZIP z wieloma plikami, dostaniesz błąd.
        </p>
        <p style={{ marginTop: "0.5rem", padding: "0.6rem 0.8rem", background: "var(--color-bg-soft, #fff8e1)", borderLeft: "4px solid var(--color-accent, #c05621)", fontSize: "0.92rem" }}>
          <strong>Stary format CSV/XLS („Najczęściej w koszyku")</strong> dalej działa, ale jest
          to <em>tryb legacy</em> — BasketBooster pokaże tylko podstawowe KPI, bez prawdziwych
          alertów stockout/dead-stock i bez par „kupowane razem". Zmień eksport na XML żeby
          zobaczyć pełen dashboard.
        </p>
      </section>

      <section className="card">
        <div
          className={"dropzone" + (dragOver ? " dropzone--over" : "") + (busy ? " dropzone--busy" : "")}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          {busy ? (
            <p>Przetwarzanie pliku… (parser → transakcje → MBA → alerty)</p>
          ) : (
            <>
              <p>Przeciągnij plik tutaj lub</p>
              <label className="btn btn--primary">
                Wybierz plik
                <input
                  type="file"
                  accept=".xml,.zip,.csv,.xls,.xlsx"
                  onChange={onPickFile}
                  style={{ display: "none" }}
                />
              </label>
              <p className="dropzone__hint">
                Akceptowane: .xml, .zip (zalecane) · .csv, .xls, .xlsx (legacy) · max 50 MB
              </p>
            </>
          )}
        </div>

        {error && (
          <p style={{ color: "var(--color-danger)", marginTop: "1rem" }}>{error}</p>
        )}

        {summary && (
          <div className="upload-summary">
            <h3>{summary.errors.length > 0 ? "Plik odrzucony ❌" : "Plik przetworzony ✅"}</h3>
            <ul>
              <li>Wierszy odczytanych: <strong>{summary.rows_processed}</strong></li>
              <li>Transakcji utworzonych: <strong>{summary.transactions_created}</strong></li>
              <li>Produktów dodanych: <strong>{summary.products_created}</strong></li>
              <li>Produktów zaktualizowanych: <strong>{summary.products_updated}</strong></li>
            </ul>
            {summary.warnings.length > 0 && (
              <details>
                <summary>Ostrzeżenia ({summary.warnings.length})</summary>
                <ul>
                  {summary.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </details>
            )}
            {summary.errors.length > 0 && (
              <details>
                <summary>Błędy ({summary.errors.length})</summary>
                <ul>
                  {summary.errors.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
      </section>

      <section className="card">
        <h2 className="card__title">Historia importów</h2>
        {imports.length === 0 ? (
          <p>Brak wcześniejszych importów dla wybranego sklepu.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Plik</th>
                <th>Status</th>
                <th>Wierszy</th>
                <th>Okres</th>
              </tr>
            </thead>
            <tbody>
              {imports.map((imp) => (
                <tr key={imp.id}>
                  <td>{formatDate(imp.created_at)}</td>
                  <td>{imp.file_name}</td>
                  <td>{STATUS_LABEL[imp.status]}</td>
                  <td>{imp.rows_processed ?? imp.rows_total ?? "—"}</td>
                  <td>
                    {imp.date_from ? new Date(imp.date_from).toLocaleDateString("pl-PL") : "—"}
                    {" – "}
                    {imp.date_to ? new Date(imp.date_to).toLocaleDateString("pl-PL") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </Layout>
  );
}
